import React, { useState, useEffect, useCallback, useRef } from "react";
import { useAtom } from "jotai";
import {
    Button,
    Link,
    Typography,
    TablePagination,
    FormLabel,
    FormControlLabel,
    Stack,
    Grid,
    TextField,
    Radio,
    RadioGroup,
    Tooltip,
    Dialog,
    DialogContent,
    DialogActions,
    DialogTitle
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useQuery } from "@tanstack/react-query";
import FormControl from "@mui/material/FormControl";
import MenuItem from "@mui/material/MenuItem";
import ListItemText from "@mui/material/ListItemText";
import Select from "@mui/material/Select";
import Checkbox from "@mui/material/Checkbox";
import AdhocBookingFlow from "../../forms/AdhocBookingFlow.js";
import { BookingsTabs } from "./BookingsTabs.js";
import { BookingsTable } from "./BookingTable";
import { UserLookup } from "../../components/UserLookup";
import { ProviderLookup } from "../../components/ProviderLookup.js";
import {
    execReadBySortkey,
    execReadBySortkeyFromToken
} from "../../modules/DBService";
import { graphqlOperation, graphql } from "../../modules/AmplifyServices";
import * as queries from "../../graphql/queries.js";
import {
    clientByUserId,
    bookingByCompany,
    bookingByProviderFilter
} from "../../queries/ListBookingsQueries.js";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import {
    selectedTabAtom,
    visibleRows,
    visibleRowsPaginated,
    tableConfigAtom,
    futureBookingsNextTokenAtom,
    pastBookingsNextTokenAtom,
    paginationAtom,
    setPageAtom,
    setPerPageAtom,
    openAddBookingDialogAtom,
    selectedClientAtom,
    selectedProviderAtom,
    ROLE_OPTIONS,
    BOOKING_STATUS_OPTIONS
} from "../../atoms/bookingsTable";
import { providersListAtom } from "../../atoms/booking";
import {
    mbxUserAtom,
    isMbxUserAdmin,
    isMbxUserProvider,
    allPendingBookingsAtom,
    storedFutureBookingsAtom,
    storedPastBookingsAtom,
    visibleBookingsAtom,
    isLoadingBookingsAtom,
    isLoadingPendingBookingsAtom,
    isMbxUserVerifiedStaff
} from "../../atoms/atoms";
import { createTheme, ThemeProvider, useTheme } from "@mui/material/styles";
import { MobileBookingsTheme } from "../../styles/MobileBookingsTheme.js";
import { grey } from "@mui/material/colors";
import { CSVLink } from "react-csv";
import moment from "moment";
import { debounce } from "lodash";
import { COLUMNS } from "./constants";
import { enUS } from "date-fns/locale";
import {
    format,
    addDays,
    addYears,
    subYears,
    isAfter,
    isBefore,
    endOfDay
} from "date-fns";
import { utcToZonedTime } from "date-fns-tz";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { DialogTheme } from "../../styles/DialogTheme.js";
import {
    setBookingDateTimeAtom,
    setDetailAtom,
    showBookingDetailsAtom
} from "../../atoms/booking.js";
import { getCurrentTimeAdjustedToNextHour } from "../../utils/SchedulerFunctions/DateNavigatorFormatter.js";
import { useNavigate } from "react-router-dom";

const ROLE_OPTIONS_LABELS = {
    [ROLE_OPTIONS.ALL]: "All",
    [ROLE_OPTIONS.ACTIVE]: "Active",
    [ROLE_OPTIONS.INACTIVE]: "Inactive"
};

const tableTheme = ({ palette }) =>
    createTheme({
        palette,
        breakpoints: {
            values: {
                xs: 0,
                sm: 700,
                md: 1280,
                lg: 1440,
                xl: 1580
            }
        },
        components: {
            MuiTableHead: {
                styleOverrides: {
                    root: {
                        borderBottom: `1px solid ${grey[300]}`,
                        backgroundColor: grey[100],
                        height: "56px",
                        "& :first-child.MuiTableCell-root, ": {
                            borderRadius: "4px 0 0 0"
                        },
                        "& :last-child.MuiTableCell-root, ": {
                            borderRadius: "0 4px  0 0"
                        },
                        "& .MuiTableCell-root": {
                            fontWeight: 700
                        }
                    }
                }
            },
            MuiTableCell: {
                styleOverrides: {
                    root: {
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        color: grey[700],
                        padding: "9px !important",
                        "&:first-of-type": {
                            padding: "0px 6px 0px 15px !important"
                        }
                    }
                }
            },
            MuiTableRow: {
                styleOverrides: {
                    root: {
                        variants: [
                            {
                                props: { variant: "cancelled" },
                                style: {
                                    pointerEvents: "none",
                                    "& .MuiTypography-root": {
                                        color: palette.text.disabled
                                    },
                                    "& .MuiSvgIcon-root": {
                                        color: `${palette.icon.disabled} !important`,
                                        pointerEvents: "auto"
                                    }
                                }
                            },
                            {
                                props: { variant: "pending" },
                                style: {
                                    backgroundColor: palette.info.lightest,
                                    "& .MuiTypography-root": {
                                        color: "black",
                                        fontWeight: 700
                                    }
                                }
                            }
                        ],
                        "&.MuiTableRow-hover:hover": {
                            backgroundColor: palette.primary.light,
                            cursor: "pointer"
                        },
                        "&:last-child .MuiTableCell-root": {
                            border: "none" // this takes care of the header row, and the last row in the list
                        }
                    }
                }
            },
            MuiChip: {
                styleOverrides: {
                    root: {
                        height: "24px",
                        width: "106px",
                        "& .MuiTypography-root": {
                            fontWeight: 700
                        }
                    }
                }
            },
            MuiTypography: {
                styleOverrides: {
                    body1: { fontSize: "14px" },
                    helperText: {
                        fontSize: "0.75rem",
                        fontWeight: "normal",
                        color: palette.helperText.main,
                        fontStyle: "italic",
                        fontFamily: ["Roboto", "sans-serif"].join(",")
                    }
                },
                variants: [
                    {
                        props: { variant: "condensed" },
                        style: {
                            lineHeight: 1.4
                        }
                    },
                    {
                        props: { variant: "link" },
                        style: {
                            textDecoration: "underline",
                            cursor: "pointer",
                            color: palette.primary.main
                        }
                    }
                ]
            },
            MuiTooltip: {
                styleOverrides: {
                    tooltip: {
                        maxWidth: "350px",
                        fontWeight: 400,
                        fontSize: "14px"
                    }
                }
            },
            MuiIconButton: {
                styleOverrides: {
                    root: {
                        padding: "0px",
                        "&:hover": {
                            backgroundColor: "transparent"
                        }
                    }
                }
            },
            MuiAlert: {
                styleOverrides: {
                    root: {
                        color: "white"
                    }
                }
            }
        }
    });

const tableConfigTheme = (palette) =>
    createTheme({
        palette,
        components: {
            MuiGrid: {
                styleOverrides: {
                    root: {
                        maxHeight: "36px",
                        minWidth: "fit-content"
                    }
                }
            },
            MuiTabs: {
                styleOverrides: {
                    root: {
                        minHeight: "46px"
                    }
                }
            },
            MuiTab: {
                styleOverrides: {
                    root: {
                        minHeight: "46px"
                    }
                }
            },
            MuiAutocomplete: {
                styleOverrides: {
                    inputRoot: {
                        height: "36px",
                        width: "250px",
                        color: palette.text.main,
                        "&:hover": {
                            borderColor: palette.inputBorder.main
                        }
                    },
                    option: {
                        fontSize: "14px"
                    },
                    noOptions: {
                        fontSize: "14px"
                    },
                    loading: {
                        fontSize: "14px"
                    }
                }
            },
            MuiOutlinedInput: {
                styleOverrides: {
                    root: {
                        minWidth: "160px",
                        padding: "0px 5px 0px 14px !important",
                        fontSize: "14px",
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: palette.inputBorder.main
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: palette.primary.main
                        }
                    }
                }
            },
            MuiInputBase: {
                styleOverrides: {
                    root: {
                        minWidth: "160px",
                        padding: "0px 5px 0px 14px !important"
                    },
                    input: {
                        padding: "7.95px 0px !important",
                        fontSize: "14px"
                    }
                }
            },
            MuiButton: {
                defaultProps: {
                    disableElevation: true
                },
                styleOverrides: {
                    root: {
                        color: "white",
                        maxHeight: "36px !important",
                        minHeight: "36px !important",
                        minWidth: "fit-content",
                        padding: "5px 18px"
                    }
                }
            },
            MuiTooltip: {
                styleOverrides: {
                    tooltip: {
                        fontWeight: 400,
                        fontSize: "14px",
                        margin: "2px !important"
                    }
                }
            },
            MuiSvgIcon: {
                styleOverrides: {
                    root: {
                        ":hover": {
                            cursor: "pointer"
                        }
                    }
                }
            }
        }
    });

const bookingStatusFilterTheme = ({ palette }) =>
    createTheme({
        palette,
        typography: {
            fontSize: "14px"
        },
        components: {
            MuiList: {
                styleOverrides: {
                    root: {
                        padding: "10px 18px 13px 18px!important",
                        width: "100%"
                    }
                }
            },
            MuiOutlinedInput: {
                styleOverrides: {
                    root: {
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: palette.inputBorder.main
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: palette.primary.main
                        }
                    }
                }
            },
            MuiFormControl: {
                styleOverrides: {
                    root: {
                        minWidth: "141px"
                    }
                }
            },
            MuiFormLabel: {
                styleOverrides: {
                    root: {
                        margin: "8px 0px"
                    }
                }
            },
            MuiFormControlLabel: {
                styleOverrides: {
                    root: {
                        height: "24px",
                        marginBottom: "0px",
                        "& .MuiButtonBase-root": {
                            padding: "1px 9px !important"
                        }
                    }
                }
            },
            MuiInputLabel: {
                styleOverrides: {
                    shrink: {
                        top: "0px !important"
                    }
                }
            },
            MuiMenuItem: {
                styleOverrides: {
                    root: {
                        padding: "0px",
                        height: "33px",
                        backgroundColor: "transparent !important"
                    }
                }
            },
            MuiSelect: {
                styleOverrides: {
                    select: {
                        color: palette.text.main,
                        top: "-7px !important",
                        padding: "8px 14px",
                        width: "auto"
                    },
                    icon: {
                        top: "7px !important"
                    }
                }
            },
            MuiCheckbox: {
                styleOverrides: {
                    root: {
                        padding: "0px 15px 0px 0px",
                        marginLeft: "-3px"
                    }
                }
            },
            MuiSvgIcon: {
                styleOverrides: {
                    root: {
                        width: "24px",
                        height: "24px"
                    },
                    checkbox: {
                        height: "18px",
                        width: "18px"
                    }
                }
            },
            MuiTypography: {
                styleOverrides: {
                    root: {
                        color: palette.text.main
                    },
                    subHeading: {
                        display: "block",
                        fontSize: "0.75rem",
                        fontWeight: "700",
                        lineHeight: "12px",
                        color: palette.helperText.main,
                        textTransform: "upperCase",
                        fontFamily: ["Roboto", "sans-serif"].join(",")
                    }
                }
            }
        }
    });

const paginationTheme = ({ palette }) =>
    createTheme({
        palette,
        components: {
            MuiInputBase: {
                styleOverrides: {
                    root: {
                        fontWeight: 700,
                        "& .MuiInputBase-input:focus": {
                            backgroundColor: "transparent"
                        }
                    }
                }
            },
            MuiToolbar: {
                styleOverrides: {
                    root: {
                        alignItems: "center",
                        "& .MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows":
                            {
                                color: palette.grey,
                                marginBottom: 0
                            },
                        "& .MuiTablePagination-actions": {
                            display: "flex",
                            gap: "0.5rem",
                            "& button": {
                                border: "1px solid rgba(0,0,0,0.2)",
                                borderRadius: "4px"
                            },
                            "& button:hover": {
                                backgroundColor: palette.lightGrey
                            }
                        }
                    }
                }
            },
            MuiTablePagination: {
                styleOverrides: {
                    actions: {
                        "& .MuiButtonBase-root": {
                            border: "none",
                            color: palette.primary.main
                        },
                        "& .Mui-disabled": {
                            color: grey[400]
                        }
                    },
                    menuItem: {
                        fontSize: "14px"
                    }
                }
            }
        }
    });
const ROWS_PER_PAGE_OPTIONS = [25, 50, 100];
export const BookingsPage = () => {
    const [, setAvailabilityError] = useState(false);
    const [, setBookingCompleted] = useState(false);
    const [loadMore, setLoadMore] = useState(false);
    const [hasTodayBookings, setHasTodayBookings] = useState(false);
    // const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

    const [mbxUser] = useAtom(mbxUserAtom);
    const [userIsProvider] = useAtom(isMbxUserProvider);
    const [tableConfig] = useAtom(tableConfigAtom);
    const futureTabsPrevTableConfigRef = useRef(tableConfig);
    const pastTabPrevTableConfigRef = useRef(tableConfig);
    const { statusFilter, dateFilter } = tableConfig;
    const [selectedClient] = useAtom(selectedClientAtom);
    const [selectedProvider] = useAtom(selectedProviderAtom);
    const [selectedTab, setSelectedTab] = useAtom(selectedTabAtom);
    const [storedFutureBookings, setStoredFutureBookings] = useAtom(
        storedFutureBookingsAtom
    );
    const [storedPastBookings, setStoredPastBookings] = useAtom(
        storedPastBookingsAtom
    );
    const [visibleBookings, setVisibleBookings] = useAtom(visibleBookingsAtom);
    const [allPendingBookings, setAllPendingBookings] = useAtom(
        allPendingBookingsAtom
    );
    const [, setIsLoadingBookings] = useAtom(isLoadingBookingsAtom);
    const [, setIsLoadingPendingBookings] = useAtom(
        isLoadingPendingBookingsAtom
    );
    const [futureBookingsNextToken, setFutureBookingsNextToken] = useAtom(
        futureBookingsNextTokenAtom
    );
    const [pastBookingsNextToken, setPastBookingsNextToken] = useAtom(
        pastBookingsNextTokenAtom
    );
    const [, setProvidersList] = useAtom(providersListAtom);

    const [displayBookings] = useAtom(visibleRowsPaginated);
    const [allViewableBookings] = useAtom(visibleRows);
    const [paginationConfig] = useAtom(paginationAtom);
    const [, setPerPageAction] = useAtom(setPerPageAtom);
    const [, setPageAction] = useAtom(setPageAtom);
    const { page, perPage } = paginationConfig;

    const [openAddBookingDialog, setOpenAddBookingDialog] = useAtom(
        openAddBookingDialogAtom
    );
    const [showBookingDetails, setShowBookingDetails] = useAtom(
        showBookingDetailsAtom
    );
    const [, setDetail] = useAtom(setDetailAtom);
    const [, setBookingDateTime] = useAtom(setBookingDateTimeAtom);

    const theme = useTheme();

    const handleOpenBookingDialog = () => {
        setDetail({
            selectedLocationId: "customerLocation"
        });
        setBookingDateTime(getCurrentTimeAdjustedToNextHour());
        setShowBookingDetails(true);
    };

    const navigate = useNavigate();

    async function getClientByUserId(userId, companyId) {
        let clientIds = [];
        const clientProfile = await graphql(
            graphqlOperation(clientByUserId, {
                userId: userId,
                filter: { companyId: { eq: companyId } }
            })
        );
        if (
            clientProfile &&
            clientProfile.data.clientByUserId.items &&
            clientProfile.data.clientByUserId.items.length
        ) {
            for (let ci of clientProfile.data.clientByUserId.items) {
                clientIds.push(ci.id);
            }
        }
        return clientIds;
    }

    const { isLoading: isLoadingPendingBookings } = useQuery({
        queryKey: [
            "get-pending-bookings",
            selectedClient?.id,
            selectedProvider?.id,
            statusFilter
        ],
        queryFn: async () => {
            try {
                let pendingBookingsList = [];
                let operation = {
                    sortDirection: selectedTab === "past" ? "DESC" : "ASC"
                };

                let PK;
                if (userIsProvider) {
                    PK = `AWAITING-P-${mbxUser.providerId}`;
                } else {
                    PK = `AWAITING-C-${mbxUser.companyId}`;
                }

                let filter = {};
                let pendingBookingsResults;

                if (selectedClient?.id) {
                    // Get clientId based on selectedClient.id (which is the userId)
                    const foundClientIds = await getClientByUserId(
                        selectedClient.id,
                        mbxUser.companyId
                    );
                    const clientId = foundClientIds[0];
                    if (clientId) {
                        filter.clientId = { eq: clientId };
                    }
                }

                if (selectedProvider?.id) {
                    filter.providerId = { eq: selectedProvider.id };
                }

                // Add the filter to the operation object
                if (Object.keys(filter).length > 0) {
                    operation.filter = filter;
                }

                pendingBookingsResults = await graphql(
                    graphqlOperation(queries.listBookingRequests, {
                        id: PK,
                        ...operation
                    })
                );

                if (pendingBookingsResults && pendingBookingsResults.data) {
                    const firstKey = Object.keys(
                        pendingBookingsResults.data
                    )[0]; // `bookingRequests`
                    const data = pendingBookingsResults.data[firstKey];

                    if (data) {
                        pendingBookingsList = data.items || [];
                    }
                }

                if (pendingBookingsList.length) {
                    pendingBookingsList = pendingBookingsList.sort((a, b) => {
                        const t1 = new Date(a.createdAt).getTime();
                        const t2 = new Date(b.createdAt).getTime();
                        return t2 - t1;
                    });
                }

                const REQs = [];

                // Check if the statusFilter array includes Pending or Expired statuses
                let pendingStatus = statusFilter.includes(
                    BOOKING_STATUS_OPTIONS.PENDING
                );
                let expiredStatus = statusFilter.includes(
                    BOOKING_STATUS_OPTIONS.EXPIRED
                );

                // Loop through each pending booking and fetch its details
                for (let pendingBooking of pendingBookingsList) {
                    const pksk = pendingBooking.recordType.split("|");

                    const req = await graphql(
                        graphqlOperation(queries.getBookingRequest, {
                            id: pksk[0],
                            recordType: `${pksk[1]}|${pksk[2]}`
                        })
                    );

                    // If the booking request is fetched successfully
                    if (req.data.getBookingRequest) {
                        const bookingStatus = req.data.getBookingRequest.status;

                        // If there is a Pending status filter, we check for relevant statuses
                        if (pendingStatus) {
                            if (
                                bookingStatus === "REQUESTED" ||
                                bookingStatus === "BROADCASTED" ||
                                bookingStatus === "BROADCAST_SCHEDULED"
                            ) {
                                req.data.getBookingRequest.data = JSON.parse(
                                    req.data.getBookingRequest.data
                                );
                                REQs.push(req.data.getBookingRequest);
                            }
                        }

                        // If there is an Expired status filter, we check for "EXPIRED" status
                        if (expiredStatus) {
                            if (bookingStatus === "EXPIRED") {
                                req.data.getBookingRequest.data = JSON.parse(
                                    req.data.getBookingRequest.data
                                );
                                REQs.push(req.data.getBookingRequest);
                            }
                        }
                    }
                }
                setAllPendingBookings(REQs);
                return REQs;
            } catch (err) {
                console.error(`Error fetching pending bookings:`, err);
            }
        }
    });
    setIsLoadingPendingBookings(isLoadingPendingBookings);

    const noSelectedUsers =
        selectedClient === null && selectedProvider === null;

    const filterToday = (bookingsToFilter) => {
        const now = new Date();
        const endOfToday = endOfDay(now);

        const todayBookings = bookingsToFilter.filter((booking) => {
            const { startdate, timezone } = booking;

            // Convert booking's start date to the local timezone
            const startDate = new Date(startdate);
            return isAfter(startDate, now) && isBefore(startDate, endOfToday);
        });

        return todayBookings;
    };

    const checkForStoredBookings = () => {
        const fetchedData = {
            past: storedPastBookings,
            upcoming: storedFutureBookings,
            today: filterToday(storedFutureBookings)
        };
        if (
            ((selectedTab === "past" &&
                pastTabPrevTableConfigRef.current === tableConfig &&
                !loadMore &&
                storedPastBookings.length) ||
                (selectedTab !== "past" &&
                    futureTabsPrevTableConfigRef.current === tableConfig &&
                    !loadMore &&
                    storedFutureBookings.length)) &&
            noSelectedUsers
        ) {
            let dataToDisplay = [...fetchedData[selectedTab]];
            if (filterToday(storedFutureBookings).length > 0) {
                setHasTodayBookings(true);
            }
            if (
                selectedTab !== "today" ||
                (selectedTab === "today" && dataToDisplay.length > 0)
            ) {
                setVisibleBookings(dataToDisplay);
            }
            // Handle empty "today" tab data
            else {
                setSelectedTab("upcoming");
                navigate("/new-bookings/upcoming");
                dataToDisplay = [...fetchedData["upcoming"]];
                setVisibleBookings(dataToDisplay);
            }
            setPageAction(0);

            return true;
        }
        return false;
    };

    const {
        isLoading: isLoadingBookings,
        isFetching,
        refetch
    } = useQuery({
        queryKey: [
            "get-bookings",
            statusFilter,
            dateFilter,
            selectedTab,
            selectedClient,
            selectedProvider
        ],
        queryFn: async () => {
            // If tableConfig didn't change and no selected users
            // and we are switching between navBar menu items or switching tabs
            // show stored data instead of refetching
            const storedBookingsFound = checkForStoredBookings();
            if (storedBookingsFound) {
                return; // Exit early if data is found, skipping the try block
            }

            try {
                let operation = {
                    opname: "bookingByCompany",
                    op: bookingByCompany,
                    id: {
                        companyId: mbxUser.companyId
                    },
                    limit: 200,
                    sortDirection: selectedTab === "past" ? "DESC" : "ASC"
                };

                if (userIsProvider) {
                    operation.opname = "bookingByProvider";
                    operation.op = bookingByProviderFilter;
                    operation.id = {
                        providerId: mbxUser.providerId
                    };
                }

                let filter = {};
                let skey = {};

                // Selected tab and date filters
                if (selectedTab !== "past") {
                    if (dateFilter.startDate && dateFilter.endDate) {
                        const startDate = dateFilter.startDate;
                        const endDate = dateFilter.endDate;
                        if (isAfter(endDate, new Date())) {
                            if (isBefore(startDate, new Date())) {
                                // If the endDate is after today and startDate is before today
                                skey = {
                                    startdateTimeblockid: {
                                        between: [
                                            {
                                                startdate: format(
                                                    new Date(),
                                                    "yyyy-MM-dd'T'HH:mm:ssXXX"
                                                )
                                            },
                                            {
                                                startdate: format(
                                                    endDate,
                                                    "yyyy-MM-dd'T'23:59:59XXX"
                                                )
                                            }
                                        ]
                                    }
                                };
                            } else if (isAfter(startDate, new Date())) {
                                // If the startDate and endDate are after today
                                skey = {
                                    startdateTimeblockid: {
                                        between: [
                                            {
                                                startdate: format(
                                                    startDate,
                                                    "yyyy-MM-dd'T'HH:mm:ssXXX"
                                                )
                                            },
                                            {
                                                startdate: format(
                                                    endDate,
                                                    "yyyy-MM-dd'T'23:59:59XXX"
                                                )
                                            }
                                        ]
                                    }
                                };
                            }
                        } else {
                            setVisibleBookings([]);
                            return;
                        }
                    } else {
                        // If no date range is provided, default to today's date to 10 years in the future
                        skey = {
                            startdateTimeblockid: {
                                between: [
                                    {
                                        startdate: format(
                                            new Date(),
                                            "yyyy-MM-dd'T'HH:mm:ssXXX"
                                        )
                                    },
                                    {
                                        startdate: format(
                                            addYears(new Date(), 10),
                                            "yyyy-MM-dd'T'23:59:59XXX"
                                        )
                                    }
                                ]
                            }
                        };
                    }
                } else if (selectedTab === "past") {
                    if (dateFilter.startDate && dateFilter.endDate) {
                        const startDate = dateFilter.startDate;
                        const endDate = dateFilter.endDate;

                        if (isBefore(startDate, new Date())) {
                            if (isBefore(endDate, new Date())) {
                                // If startDate and endDate are before today
                                skey = {
                                    startdateTimeblockid: {
                                        between: [
                                            {
                                                startdate: format(
                                                    startDate,
                                                    "yyyy-MM-dd'T'HH:mm:ssXXX"
                                                )
                                            },
                                            {
                                                startdate: format(
                                                    endDate,
                                                    "yyyy-MM-dd'T'23:59:59XXX"
                                                )
                                            }
                                        ]
                                    }
                                };
                            } else if (isAfter(endDate, new Date())) {
                                // If startDate is before today and endDate is after today,
                                skey = {
                                    startdateTimeblockid: {
                                        between: [
                                            {
                                                startdate: format(
                                                    startDate,
                                                    "yyyy-MM-dd'T'HH:mm:ssXXX"
                                                )
                                            },
                                            {
                                                startdate: format(
                                                    new Date(),
                                                    "yyyy-MM-dd'T'HH:mm:ssXXX"
                                                )
                                            }
                                        ]
                                    }
                                };
                            }
                        } else {
                            setVisibleBookings([]);
                            return;
                        }
                    } else {
                        // If no date range is provided, default to 10 years in the past to today
                        skey = {
                            startdateTimeblockid: {
                                between: [
                                    {
                                        startdate: format(
                                            subYears(new Date(), 10),
                                            "yyyy-MM-dd'T'00:00:00XXX"
                                        )
                                    },
                                    {
                                        startdate: format(
                                            new Date(),
                                            "yyyy-MM-dd'T'HH:mm:ssXXX"
                                        )
                                    }
                                ]
                            }
                        };
                    }
                }
                operation.skey = skey;

                // Booking status filter
                if (statusFilter.length > 0) {
                    let or = [];
                    for (let status of statusFilter) {
                        if (
                            status !== BOOKING_STATUS_OPTIONS.PENDING &&
                            status !== BOOKING_STATUS_OPTIONS.EXPIRED
                        ) {
                            if (status === BOOKING_STATUS_OPTIONS.NOSHOW) {
                                or.push({
                                    status: { eq: "NOSHOW" }
                                });
                            } else {
                                or.push({
                                    status: { eq: status.toUpperCase() }
                                });
                            }
                        }
                    }
                    if (or.length > 0) {
                        filter = {
                            ...filter,
                            or: or
                        };
                    } else {
                        setVisibleBookings([]);
                        return;
                    }
                } else {
                    setVisibleBookings([]);
                    return;
                }

                // Selected client filter
                if (selectedClient?.id) {
                    const foundClientIds = await getClientByUserId(
                        selectedClient.id,
                        mbxUser.companyId
                    );
                    const clientId = foundClientIds[0];

                    if (clientId) {
                        filter.clientId = { eq: clientId };
                    }
                }

                // Selected provider filter
                if (selectedProvider?.id) {
                    filter.providerId = { eq: selectedProvider.id };
                }

                if (Object.keys(filter).length > 0) {
                    operation.filter = filter;
                }

                const bookingsResults = await execReadBySortkeyFromToken(
                    operation,
                    noSelectedUsers &&
                        !(dateFilter.startDate && dateFilter.endDate)
                        ? selectedTab === "past"
                            ? pastBookingsNextToken
                            : futureBookingsNextToken
                        : null,
                    []
                );
                if (bookingsResults && bookingsResults.items) {
                    const newBookings = bookingsResults.items || [];
                    const todayBookings = filterToday(newBookings);
                    if (todayBookings?.length > 0) {
                        setHasTodayBookings(true);
                    }

                    if (isFetching && noSelectedUsers) {
                        if (selectedTab === "past") {
                            setStoredPastBookings(newBookings);
                            setVisibleBookings(newBookings);
                            pastTabPrevTableConfigRef.current = tableConfig;
                        } else {
                            setStoredFutureBookings(newBookings);
                            if (selectedTab === "upcoming") {
                                setVisibleBookings(newBookings);
                            } else {
                                // If no bookings for today, hide the today tab
                                if (todayBookings?.length > 0) {
                                    setVisibleBookings(
                                        filterToday(newBookings)
                                    );
                                } else {
                                    setSelectedTab("upcoming");
                                    navigate("/new-bookings/upcoming");
                                    setVisibleBookings(newBookings);
                                }
                            }
                            futureTabsPrevTableConfigRef.current = tableConfig;
                        }
                    } else {
                        if (noSelectedUsers) {
                            if (selectedTab === "past") {
                                if (
                                    pastTabPrevTableConfigRef.current !==
                                    tableConfig
                                ) {
                                    setStoredPastBookings(newBookings);
                                    setVisibleBookings(newBookings);
                                    pastTabPrevTableConfigRef.current =
                                        tableConfig;
                                } else {
                                    setStoredPastBookings((prevBookings) => [
                                        ...prevBookings,
                                        ...newBookings
                                    ]);
                                    setVisibleBookings((prevBookings) => [
                                        ...prevBookings,
                                        ...newBookings
                                    ]);
                                }
                            } else {
                                if (
                                    futureTabsPrevTableConfigRef.current !==
                                    tableConfig
                                ) {
                                    setStoredFutureBookings(newBookings);
                                    if (selectedTab === "upcoming") {
                                        setVisibleBookings(newBookings);
                                    } else {
                                        // If no bookings for today, hide the today tab
                                        if (todayBookings?.length > 0) {
                                            setVisibleBookings(
                                                filterToday(newBookings)
                                            );
                                        } else {
                                            setSelectedTab("upcoming");
                                            navigate("/new-bookings/upcoming");
                                        }
                                    }
                                    futureTabsPrevTableConfigRef.current =
                                        tableConfig;
                                } else {
                                    setStoredFutureBookings((prevBookings) => [
                                        ...prevBookings,
                                        ...newBookings
                                    ]);
                                    const currentFutureBookings = [
                                        ...visibleBookings,
                                        ...newBookings
                                    ];
                                    if (selectedTab === "upcoming") {
                                        setVisibleBookings(
                                            currentFutureBookings
                                        );
                                    } else {
                                        // If no bookings for today, hide the today tab
                                        if (todayBookings?.length > 0) {
                                            setVisibleBookings(
                                                filterToday(
                                                    currentFutureBookings
                                                )
                                            );
                                        } else {
                                            setSelectedTab("upcoming");
                                            navigate("/new-bookings/upcoming");
                                        }
                                    }
                                }
                            }
                        } else {
                            if (selectedTab === "past") {
                                setVisibleBookings(newBookings);
                            } else if (selectedTab === "upcoming") {
                                setVisibleBookings(newBookings);
                            } else {
                                setVisibleBookings(filterToday(newBookings));
                            }
                        }
                    }

                    const newNextToken = bookingsResults.token;

                    // depending on which tab we are on, set the correct booking next token
                    if (selectedTab === "past") {
                        newNextToken
                            ? setPastBookingsNextToken(newNextToken)
                            : setPastBookingsNextToken(null);
                    } else if (selectedTab !== "past") {
                        newNextToken
                            ? setFutureBookingsNextToken(newNextToken)
                            : setFutureBookingsNextToken(null);
                    }
                }
            } catch (err) {
                console.error(`Error fetching bookings:`, err);
            }
        }
    });

    const { data: providers } = useQuery({
        queryKey: ["get-providers-list"],
        queryFn: async () => {
            try {
                let providersList = await execReadBySortkey({
                    opname: "providerByCompany",
                    op: /* GraphQL */ `
                        query ProviderByCompany(
                            $companyId: String
                            $firstnameLastnameCreatedAt: ModelProviderByCompanyCompositeKeyConditionInput
                            $sortDirection: ModelSortDirection
                            $filter: ModelProviderFilterInput
                            $limit: Int
                            $nextToken: String
                        ) {
                            providerByCompany(
                                companyId: $companyId
                                firstnameLastnameCreatedAt: $firstnameLastnameCreatedAt
                                sortDirection: $sortDirection
                                filter: $filter
                                limit: $limit
                                nextToken: $nextToken
                            ) {
                                items {
                                    id
                                    firstname
                                    lastname
                                    emailaddress
                                    phone
                                    timezone
                                    user {
                                        id
                                    }
                                }

                                nextToken
                            }
                        }
                    `,
                    id: {
                        companyId: mbxUser.companyId
                    },
                    filter: {
                        and: [
                            { active: { ne: false } },
                            { deleted: { ne: true } }
                        ]
                    }
                });

                if (
                    providersList &&
                    !providersList.error &&
                    providersList.items &&
                    providersList.items.length
                ) {
                    providersList.items.forEach((item) => {
                        item.name = `${item.firstname} ${item.lastname}`;
                        item.email = item.emailaddress;
                    });
                    return providersList.items;
                }
                return [];
            } catch (e) {
                console.log(e);
            }
        },
        onSuccess: (providersList) => {
            setProvidersList(providersList);
        }
    });

    setIsLoadingBookings(isLoadingBookings);

    const handlePageChange = (event, newPage) => {
        setPageAction(newPage);
        window.scrollTo({ top: 0, behavior: "smooth" });

        if (
            newPage * perPage >=
                allPendingBookings.length + visibleBookings.length - perPage &&
            ((selectedTab === "past" && pastBookingsNextToken) ||
                (selectedTab !== "past" && futureBookingsNextToken))
        ) {
            setLoadMore(true);
        }
    };

    // Call refetch when loadMore is set to true
    useEffect(() => {
        if (loadMore) {
            refetch();
            setLoadMore(false);
        }
    }, [loadMore]);

    const resetAvailabilityError = () => {
        setAvailabilityError(false);
    };

    return (
        <ThemeProvider theme={tableTheme(theme)}>
            <TableHeader
                onOpenAddBookingDialog={handleOpenBookingDialog}
                hasTodayBookings={hasTodayBookings}
                refetchBookings={() => refetch()}
                filterToday={filterToday}
            />

            <BookingsTable bookings={displayBookings} />
            <ThemeProvider theme={paginationTheme(theme)}>
                <TablePagination
                    count={allViewableBookings?.length || 0} // Set this to the total count of bookings if available
                    component="div"
                    page={page}
                    rowsPerPage={perPage}
                    onPageChange={handlePageChange}
                    onRowsPerPageChange={({ target: { value } }) => {
                        setPerPageAction(value);
                        setPageAction(0);
                    }}
                    rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                    labelRowsPerPage=""
                />
            </ThemeProvider>
        </ThemeProvider>
    );
};

const TableHeader = ({ onOpenAddBookingDialog, hasTodayBookings }) => {
    const [, setSelectedClient] = useAtom(selectedClientAtom);
    const [, setSelectedProvider] = useAtom(selectedProviderAtom);
    const [openDateFilter, setOpenDateFilter] = useState(false);
    const [openStartDate, setOpenStartDate] = useState(false);
    const [openEndDate, setOpenEndDate] = useState(false);
    const [dateError, setDateError] = useState("");
    const [tableConfig, setTableConfig] = useAtom(tableConfigAtom);
    const [, setPageAction] = useAtom(setPageAtom);

    const [mbxUser] = useAtom(mbxUserAtom);
    const [isAdminLoggedIn] = useAtom(isMbxUserAdmin);
    const [isVerifiedStaff] = useAtom(isMbxUserVerifiedStaff);
    const companyId = mbxUser.companyId;

    const themePalette = useTheme().palette;
    const theme = useTheme();

    const [startDate, setStartDate] = useState(new Date());
    const [endDate, setEndDate] = useState(addDays(new Date(), 7));

    const handleApplyDateFilter = () => {
        setTableConfig({
            ...tableConfig,
            dateFilter: {
                startDate,
                endDate
            }
        });
        setOpenDateFilter(false);
    };
    return (
        <ThemeProvider theme={tableConfigTheme(themePalette)}>
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
            >
                <Stack
                    display="flex"
                    direction="column"
                    alignItems="center"
                    gap={"2px"}
                >
                    <Grid
                        container
                        item
                        gap="15px"
                        sx={{ alignItems: "center" }}
                    >
                        <ThemeProvider theme={MobileBookingsTheme(theme)}>
                            <Typography variant="h1">Bookings</Typography>
                        </ThemeProvider>
                        {isAdminLoggedIn && (
                            <CSVLink
                                data={[]}
                                headers={COLUMNS}
                                filename={`Bookings_${moment(Date.now()).format(
                                    "YYYY-MM-DD"
                                )}.csv`}
                            >
                                <Tooltip title="Export">
                                    <CloudDownloadIcon color="primary" />
                                </Tooltip>
                            </CSVLink>
                        )}
                    </Grid>
                    <BookingsTabs hasTodayBookings={hasTodayBookings} />
                </Stack>
                <Stack
                    display="flex"
                    direction="row"
                    justifyContent="flex-end"
                    alignItems="center"
                    gap="18px"
                >
                    <Grid item>
                        <Link
                            align="center"
                            onClick={() => setOpenDateFilter(true)}
                        >
                            <ThemeProvider theme={MobileBookingsTheme(theme)}>
                                <Typography
                                    variant="link"
                                    color="primary"
                                    sx={{
                                        "&:hover": { textDecoration: "none" }
                                    }}
                                >
                                    <b>Date range</b>
                                </Typography>
                            </ThemeProvider>
                        </Link>
                    </Grid>

                    {openDateFilter && (
                        <ThemeProvider theme={DialogTheme(theme)}>
                            <Dialog
                                open={openDateFilter}
                                onClose={() => {
                                    setOpenDateFilter(false);
                                    setOpenStartDate(false);
                                    setOpenEndDate(false);
                                }}
                            >
                                <DialogTitle sx={{ paddingBottom: "15px" }}>
                                    Bookings date range
                                </DialogTitle>
                                <DialogContent>
                                    <LocalizationProvider
                                        dateAdapter={AdapterDateFns}
                                        adapterLocale={{
                                            ...enUS,
                                            options: {
                                                ...enUS.options,
                                                weekStartsOn: 1
                                            }
                                        }}
                                    >
                                        <Grid
                                            container
                                            spacing={2.25}
                                            sx={{ marginTop: "0px" }}
                                        >
                                            <Grid item xs={6} sm={6}>
                                                <DatePicker
                                                    autoFocus
                                                    id="start-date"
                                                    name="startDate"
                                                    size="small"
                                                    label="Start date"
                                                    value={startDate}
                                                    fullWidth
                                                    inputFormat="MMM d, yyyy"
                                                    open={openStartDate}
                                                    onOpen={() =>
                                                        setOpenStartDate(true)
                                                    }
                                                    onClose={() =>
                                                        setOpenStartDate(false)
                                                    }
                                                    onChange={(newValue) => {
                                                        setStartDate(newValue);
                                                        if (
                                                            newValue &&
                                                            endDate &&
                                                            newValue > endDate
                                                        ) {
                                                            setDateError(
                                                                "Start date cannot be after end date"
                                                            );
                                                        } else {
                                                            setDateError("");
                                                        }
                                                    }}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            fullWidth
                                                            error={dateError}
                                                            inputProps={{
                                                                ...params.inputProps,
                                                                readOnly: true
                                                            }}
                                                            onClick={() =>
                                                                setOpenStartDate(
                                                                    true
                                                                )
                                                            }
                                                            sx={{
                                                                "& .MuiInputAdornment-root":
                                                                    {
                                                                        display:
                                                                            "none"
                                                                    },
                                                                "& fieldset": {
                                                                    "& legend":
                                                                        {
                                                                            width: "54px"
                                                                        }
                                                                }
                                                            }}
                                                        />
                                                    )}
                                                />
                                            </Grid>
                                            <Grid item xs={6} sm={6}>
                                                <DatePicker
                                                    id="end-date"
                                                    name="endDate"
                                                    size="small"
                                                    label="End date"
                                                    value={endDate}
                                                    fullWidth
                                                    inputFormat="MMM d, yyyy"
                                                    open={openEndDate}
                                                    onOpen={() =>
                                                        setOpenEndDate(true)
                                                    }
                                                    onClose={() =>
                                                        setOpenEndDate(false)
                                                    }
                                                    onChange={(newValue) => {
                                                        setEndDate(newValue);
                                                        if (
                                                            startDate &&
                                                            newValue &&
                                                            startDate > newValue
                                                        ) {
                                                            setDateError(
                                                                "End date cannot be before start date."
                                                            );
                                                        } else {
                                                            setDateError("");
                                                        }
                                                    }}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            fullWidth
                                                            inputProps={{
                                                                ...params.inputProps,
                                                                readOnly: true
                                                            }}
                                                            onClick={() =>
                                                                setOpenEndDate(
                                                                    true
                                                                )
                                                            }
                                                            sx={{
                                                                "& .MuiInputAdornment-root":
                                                                    {
                                                                        display:
                                                                            "none"
                                                                    },
                                                                "& fieldset": {
                                                                    "& legend":
                                                                        {
                                                                            width: "50px"
                                                                        }
                                                                }
                                                            }}
                                                        />
                                                    )}
                                                />
                                            </Grid>
                                            {dateError && (
                                                <Grid
                                                    variant="errorTextContainer"
                                                    item
                                                    xs={12}
                                                    sm={12}
                                                >
                                                    <Typography variant="errorText">
                                                        {dateError}
                                                    </Typography>
                                                </Grid>
                                            )}
                                        </Grid>
                                    </LocalizationProvider>
                                </DialogContent>
                                <DialogActions>
                                    <Grid
                                        container
                                        item
                                        gap={"18px"}
                                        sx={{
                                            display: "flex",
                                            justifyContent: "flex-end"
                                        }}
                                    >
                                        <Button
                                            variant="cancelButton"
                                            onClick={() => {
                                                setOpenDateFilter(false);
                                                setOpenStartDate(false);
                                                setOpenEndDate(false);
                                            }}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={handleApplyDateFilter}
                                            disabled={!!dateError}
                                        >
                                            Apply
                                        </Button>
                                    </Grid>
                                </DialogActions>
                            </Dialog>
                        </ThemeProvider>
                    )}

                    <Grid item>
                        <BookingStatusFilter />
                    </Grid>

                    <UserLookup
                        companyId={companyId}
                        setSelectedUser={(u) => {
                            setPageAction(0);
                            setSelectedClient(u || null);
                        }}
                    />

                    {isVerifiedStaff && (
                        <ProviderLookup
                            companyId={mbxUser.companyId}
                            setSelectedProvider={(u) => {
                                setPageAction(0);
                                setSelectedProvider(u || null);
                            }}
                        />
                    )}

                    {isVerifiedStaff && (
                        <Button
                            textTransform="uppercase"
                            variant="contained"
                            color="primary"
                            startIcon={<AddIcon sx={{ color: "white" }} />}
                            onClick={onOpenAddBookingDialog}
                        >
                            <b> Add Booking</b>
                        </Button>
                    )}
                </Stack>
            </Stack>
        </ThemeProvider>
    );
};

export default function BookingStatusFilter() {
    const [, setPageAction] = useAtom(setPageAtom);
    const [, setFutureBookingsNextToken] = useAtom(futureBookingsNextTokenAtom);
    const [, setPastBookingsNextToken] = useAtom(pastBookingsNextTokenAtom);
    const [tableConfig, setTableConfig] = useAtom(tableConfigAtom);
    const { statusFilter, roleFilter } = tableConfig;
    const [tempBookingStatus, setTempBookingStatus] = useState(statusFilter);
    const [openBookingsFilter, setOpenBookingsFilters] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const theme = useTheme();
    const bookingsFiltersRef = useRef(null);

    const handleBookingStatusChange = (event) => {
        const {
            target: { value }
        } = event;

        const newBookingStatus =
            typeof value === "string" ? value.split(",") : value;
        setTempBookingStatus(newBookingStatus);
        setPageAction(0);
        setFutureBookingsNextToken(null);
        setPastBookingsNextToken(null);

        debouncedHandleFilterClick(newBookingStatus);
    };

    const debouncedHandleFilterClick = useCallback(
        debounce((newBookingStatus) => {
            setTableConfig({
                ...tableConfig,
                statusFilter: newBookingStatus
            });
            setOpenBookingsFilters(false);
            bookingsFiltersRef.current.classList.remove("Mui-focused");
        }, 850),
        []
    );

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
        setOpenBookingsFilters(true);
        bookingsFiltersRef.current.classList.add("Mui-focused");
    };

    const handleClose = () => {
        setOpenBookingsFilters(false);
        bookingsFiltersRef.current.classList.remove("Mui-focused");
    };

    return (
        <ThemeProvider theme={bookingStatusFilterTheme(theme)}>
            <FormControl fullWidth>
                <Select
                    ref={bookingsFiltersRef}
                    sx={{
                        display: "inline-block",
                        verticalAlign: "middle",
                        justifyContent: "center",
                        alignItems: "center",
                        maxHeight: "36px !important"
                    }}
                    labelId="demo-multiple-checkbox-label"
                    id="demo-multiple-checkbox"
                    multiple
                    value={tempBookingStatus}
                    onOpen={handleClick}
                    onClose={handleClose}
                    open={openBookingsFilter}
                    onChange={handleBookingStatusChange}
                    displayEmpty
                    renderValue={() => "Filters"}
                    MenuProps={{
                        PaperProps: {
                            style: {
                                width: anchorEl ? anchorEl.offsetWidth : "auto"
                            }
                        }
                    }}
                >
                    <FormLabel id="demo-radio-buttons-group-label">
                        <Typography variant="subHeading">Status</Typography>
                    </FormLabel>
                    {Object.values(BOOKING_STATUS_OPTIONS).map((option) => (
                        <MenuItem key={option} value={option} disableRipple>
                            <Checkbox
                                disableRipple
                                checked={tempBookingStatus.indexOf(option) > -1}
                            />
                            <ListItemText primary={option} />
                        </MenuItem>
                    ))}

                    <FormLabel id="demo-radio-buttons-group-label">
                        <Typography variant="subHeading">Providers</Typography>
                    </FormLabel>
                    <RadioGroup
                        aria-labelledby="demo-radio-buttons-group-label"
                        defaultValue={ROLE_OPTIONS.ALL}
                        value={roleFilter}
                        onChange={({ target: { value } }) =>
                            setTableConfig({
                                ...tableConfig,
                                roleFilter: value
                            })
                        }
                    >
                        {Object.values(ROLE_OPTIONS).map((option) => (
                            <FormControlLabel
                                key={option}
                                value={option}
                                control={
                                    <Radio
                                        disableRipple
                                        sx={{
                                            "& .MuiSvgIcon-root": {
                                                width: "1.1rem",
                                                height: "1.1rem"
                                            }
                                        }}
                                    />
                                }
                                label={ROLE_OPTIONS_LABELS[option]}
                            />
                        ))}
                    </RadioGroup>
                </Select>
            </FormControl>
        </ThemeProvider>
    );
}
